import React, { useState } from "react";
import {
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Tooltip,
} from "antd";
import locale from "antd/es/date-picker/locale/pt_BR";
import { useQuery } from "react-query";
import { AxiosEmployeeAPI } from "./Api";
import { AxiosAreasAPI } from "../areas/Api";
import { AxiosPositionsAPI } from "../positions/Api";
import SystemPermissionsToggles from "./components/SystemPermissionsToggles";
import { useSettingValue, useDebouncedSearch } from "../utils/Hooks";
import { formatCPF, maxValueRule } from "../utils/input";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Trans } from "common/components/MockedTrans.tsx";

const useTranslation = () => {
  return { t: (text) => text };
};

const AddEmployeeForm = ({ onFinish, form }) => {
  const { t } = useTranslation();
  const [, debouncedSearch, setSearch] = useDebouncedSearch();
  const [isSearching, setIsSearching] = useState(true);
  const { data: employees } = useQuery(
    ["employees", debouncedSearch],
    () => AxiosEmployeeAPI.getAllAccessible({ search: debouncedSearch }, {}),
    { onSettled: () => setIsSearching(false) },
  );
  const { data: areas } = useQuery(
    ["areas", debouncedSearch],
    () => AxiosAreasAPI.getAll({ search: debouncedSearch }, {}),
    { onSettled: () => setIsSearching(false) },
  );
  const { data: positions } = useQuery(
    ["positions", debouncedSearch],
    () => AxiosPositionsAPI.getAll({ search: debouncedSearch }, {}),
    { onSettled: () => setIsSearching(false) },
  );

  const isIntegrationAuthEnabled = useSettingValue("INTEGRATION_AUTH_ENABLED");
  const isUsingCustomKey = useSettingValue(
    "T006_ALLOW_FIELD_SELECTION_AS_EMPLOYEE_KEY",
  );
  const customKey = useSettingValue("EMPLOYEE_CUSTOM_KEY");
  const cpfMode = isUsingCustomKey && customKey === "cpf";

  const handleCpfChange = (e) => {
    const { value } = e.target;
    const formattedCpf = formatCPF(value);
    form.setFieldValue("cpf", formattedCpf);
    if (cpfMode) {
      form.setFieldValue("employee_code", formattedCpf);
    }
  };

  const handleEmailChange = (e) => {
    if (isUsingCustomKey && customKey === "email") {
      const { value } = e.target;
      form.setFieldValue("employee_code", value);
    }
  };

  const emailFormItem = (
    <Form.Item
      label={t("E-mail:")}
      name="email"
      rules={[
        {
          type: "email",
          message: t("Por favor insira um e-mail válido."),
        },
        {
          required: true,
          message: t("Por favor insira um e-mail."),
        },
        maxValueRule(254),
      ]}
      onChange={handleEmailChange}
    >
      <Input />
    </Form.Item>
  );

  const employeeCodeFormItem = (
    <Form.Item
      label={t("Código:")}
      name="employee_code"
      rules={[
        { required: true, message: t("Por favor insira o código") },
        maxValueRule(254),
      ]}
    >
      <Input disabled={isUsingCustomKey && customKey !== "employee_code"} />
    </Form.Item>
  );

  const usernameFormItem = (
    <Form.Item
      label={
        <span>
          <Trans>Nome de usuário</Trans>{" "}
          <Tooltip
            title={t(
              "Este será o nome de usuário utilizado pelo funcionário para acessar nossos sistemas.",
            )}
          >
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      }
      name="username"
      rules={[
        { required: true, message: t("Por favor insira o nome de usuário.") },
        maxValueRule(200),
      ]}
    >
      <Input />
    </Form.Item>
  );

  return (
    <Form layout="vertical" onFinish={onFinish} form={form}>
      <Row gutter={50} justify="start">
        <Col span={9}>
          {" "}
          <Form.Item
            label={t("Nome")}
            name="first_name"
            rules={[
              { required: true, message: t("Por favor insira o nome.") },
              maxValueRule(200),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={9}>
          <Form.Item
            label={t("Sobrenome")}
            name="last_name"
            rules={[
              { required: true, message: t("Por favor insira o sobrenome.") },
              maxValueRule(200),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={50} justify="start">
        {cpfMode ? (
          <Col span={18}>
            {usernameFormItem} {emailFormItem}{" "}
            <Form.Item
              label={t("CPF")}
              name="cpf"
              rules={[
                { required: true, message: t("Por favor insira o CPF.") },
                maxValueRule(14),
              ]}
            >
              <Input placeholder="000.000.000-00" onChange={handleCpfChange} />
            </Form.Item>{" "}
            {employeeCodeFormItem}{" "}
          </Col>
        ) : (
          <Col span={9}>
            {usernameFormItem} {emailFormItem} {employeeCodeFormItem}
          </Col>
        )}
      </Row>
      <Row>
        <Col>
          {" "}
          <Form.Item
            name="start_date"
            label="Data de contratação"
            rules={[
              {
                required: true,
                message: "Por favor insira a data de contratação.",
              },
            ]}
          >
            <DatePicker
              locale={locale}
              placeholder={"Selecione uma data"}
              format={"DD/MM/YYYY"}
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider style={{ width: "100%" }} />
      <Row>
        <Col span={8}>
          <FormSearchSelect
            label={t("Gestor:")}
            name={"manager"}
            options={employees}
            isSearching={isSearching}
            setSearch={setSearch}
            setIsSearching={setIsSearching}
          />
        </Col>
        <Col span={8}>
          <FormSearchSelect
            label={t("Área:")}
            name={"area"}
            options={areas}
            isSearching={isSearching}
            setSearch={setSearch}
            setIsSearching={setIsSearching}
          />
        </Col>
        <Col span={8}>
          <FormSearchSelect
            label={t("Cargo:")}
            name={"position"}
            options={positions}
            isSearching={isSearching}
            setSearch={setSearch}
            setIsSearching={setIsSearching}
          />
        </Col>
      </Row>

      {isIntegrationAuthEnabled && (
        <>
          <Divider style={{ width: "100%" }} />
          <SystemPermissionsToggles />
        </>
      )}
    </Form>
  );
};

export default AddEmployeeForm;

const FormSearchSelect = ({
  label,
  name,
  options,
  isSearching,
  setSearch,
  setIsSearching,
}) => {
  const { t } = useTranslation();
  const { Option } = Select;
  return (
    <Form.Item label={label} name={name}>
      <Select
        placeholder={t("Selecione")}
        showSearch
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
        style={{
          width: 120,
        }}
        onSearch={(value) => {
          setSearch(value);
          setIsSearching(true);
        }}
        notFoundContent={
          isSearching ? <Spin size="small" /> : t("Nenhum item encontrado")
        }
      >
        {!isSearching &&
          options?.results.map((option) => (
            <Option key={option.id} value={option.api_url}>
              {option.name}
            </Option>
          ))}
      </Select>
    </Form.Item>
  );
};
